/**
 * gez-header.js — Menu v2 drawer toggle — v2 2026-05-23 cache-bust
 *
 * IIFE-scoped. Handles open/close of .gez-menu-drawer and the
 * backdrop + hamburger animation. Does NOT touch the cart pill
 * or cart-drawer — those are wired independently via Dawn's
 * cart-drawer.js which hooks on id="cart-icon-bubble".
 *
 * Selectors must match snippets/gez-header.liquid:
 *   #gezNavToggle      — hamburger button
 *   #gezMenuDrawer     — <nav> drawer element
 *   #gezMenuBackdrop   — backdrop <div>
 *   [data-menu-link]   — all drawer <a> items (close drawer on click)
 *
 * Re-initialises on Shopify section:load so theme editor live
 * editing does not break the toggle.
 */

(function () {
  'use strict';

  /**
   * @param {HTMLElement} navToggle
   * @param {HTMLElement} menuDrawer
   * @param {HTMLElement} menuBackdrop
   */
  function initMenuDrawer(navToggle, menuDrawer, menuBackdrop) {
    if (!navToggle || !menuDrawer || !menuBackdrop) return;

    // --- Drawer search references ---
    const drawerSearch      = menuDrawer.querySelector('[data-drawer-search]');
    const drawerSearchToggle = menuDrawer.querySelector('[data-drawer-search-toggle]');
    const drawerSearchClose  = menuDrawer.querySelector('[data-drawer-search-close]');
    const drawerSearchForm   = drawerSearch ? drawerSearch.querySelector('.gez-menu-drawer__search-form') : null;
    const drawerSearchInput  = drawerSearch ? drawerSearch.querySelector('.gez-menu-drawer__search-input') : null;

    function resetDrawerSearch() {
      if (!drawerSearch) return;
      drawerSearch.classList.remove('is-expanded');
      if (drawerSearchToggle) drawerSearchToggle.setAttribute('aria-expanded', 'false');
      if (drawerSearchForm)   drawerSearchForm.setAttribute('hidden', '');
      if (drawerSearchInput)  drawerSearchInput.value = '';
    }

    if (drawerSearchToggle) {
      drawerSearchToggle.addEventListener('click', function () {
        drawerSearch.classList.add('is-expanded');
        drawerSearchToggle.setAttribute('aria-expanded', 'true');
        drawerSearchForm.removeAttribute('hidden');
        // Small delay so the CSS transition completes before focus
        setTimeout(function () { if (drawerSearchInput) drawerSearchInput.focus(); }, 50);
      });
    }

    if (drawerSearchClose) {
      drawerSearchClose.addEventListener('click', function () {
        resetDrawerSearch();
        if (drawerSearchToggle) drawerSearchToggle.focus();
      });
    }

    function openMenu() {
      menuDrawer.classList.add('is-open');
      menuBackdrop.classList.add('is-open');
      navToggle.classList.add('is-open');
      navToggle.setAttribute('aria-expanded', 'true');
      navToggle.setAttribute('aria-label', navToggle.dataset.labelClose || 'Close menu');
      menuDrawer.setAttribute('aria-hidden', 'false');
      menuBackdrop.setAttribute('aria-hidden', 'false');
      document.body.classList.add('gez-menu-open');

      // Move focus to the first focusable item inside the drawer
      const firstFocusable = menuDrawer.querySelector('a[href], button:not([disabled])');
      if (firstFocusable) firstFocusable.focus();
    }

    function closeMenu() {
      menuDrawer.classList.remove('is-open');
      menuBackdrop.classList.remove('is-open');
      navToggle.classList.remove('is-open');
      navToggle.setAttribute('aria-expanded', 'false');
      navToggle.setAttribute('aria-label', navToggle.dataset.labelOpen || 'Open menu');
      menuDrawer.setAttribute('aria-hidden', 'true');
      menuBackdrop.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('gez-menu-open');
      // Reset drawer search to collapsed state on every close
      resetDrawerSearch();
    }

    function toggleMenu() {
      if (menuDrawer.classList.contains('is-open')) {
        closeMenu();
      } else {
        openMenu();
      }
    }

    navToggle.addEventListener('click', toggleMenu);
    menuBackdrop.addEventListener('click', closeMenu);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menuDrawer.classList.contains('is-open')) {
        closeMenu();
        navToggle.focus();
      }
    });

    // Close drawer when any drawer link is clicked (navigation occurring)
    menuDrawer.querySelectorAll('[data-menu-link]').forEach(function (link) {
      link.addEventListener('click', closeMenu);
    });

    // Store label strings on the toggle button for easy access in open/close
    navToggle.dataset.labelOpen  = navToggle.getAttribute('aria-label') || 'Open menu';
    navToggle.dataset.labelClose = 'Close menu';
  }

  function init() {
    const navToggle    = document.getElementById('gezNavToggle');
    const menuDrawer   = document.getElementById('gezMenuDrawer');
    const menuBackdrop = document.getElementById('gezMenuBackdrop');
    initMenuDrawer(navToggle, menuDrawer, menuBackdrop);
  }

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor section:load so live edits
  // in the editor do not break the toggle
  document.addEventListener('shopify:section:load', function (e) {
    if (e.detail && e.detail.sectionId) {
      init();
    }
  });

})();
